import { useEffect, useMemo, useRef, useState } from 'react';
import { bookingReasons, timeSlots } from '../data/site.js';
import { fetchBookingAvailability, submitRequest } from '../services/submissions.js';

const CLINIC_TIME_ZONE = import.meta.env.VITE_CLINIC_TIMEZONE || 'Asia/Kolkata';

function createInitialValues(initialReason = '') {
  return {
    reason: initialReason,
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    consent: false,
    website: '',
    formStartedAt: Date.now()
  };
}

function createAvailabilityState(date = '') {
  return {
    date,
    loading: false,
    enabled: false,
    unavailableSlots: [],
    message: ''
  };
}

function clinicTimeParts(date = new Date()) {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  };

  try {
    const formatter = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: CLINIC_TIME_ZONE });
    return Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  } catch {
    const formatter = new Intl.DateTimeFormat('en-GB', options);
    return Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
  }
}

function todayIso(date = new Date()) {
  const parts = clinicTimeParts(date);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function minutesFromTime(value) {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

function currentMinutes(date = new Date()) {
  const parts = clinicTimeParts(date);
  return Number(parts.hour) * 60 + Number(parts.minute);
}

function isPastTimeSlot(date, time, now = new Date()) {
  if (!date || !time || date !== todayIso(now)) return false;
  return minutesFromTime(time) <= currentMinutes(now);
}

function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isPhone(value) {
  return /^[\d\s+\-()]{7,}$/.test(value);
}

function isClinicClosedDate(date) {
  if (!date) return false;
  const selectedDate = new Date(`${date}T00:00:00`);
  return selectedDate.getDay() === 3;
}

function statusTitle(status) {
  if (!status) return '';
  if (status.ok) return 'Appointment requested';
  if (status.code === 'RATE_LIMITED') return 'Too many attempts';
  if (status.code === 'SLOT_UNAVAILABLE') return 'Time no longer available';
  if (status.code === 'REQUEST_TIMEOUT') return 'Confirmation took too long';
  return 'Request could not be sent';
}

export default function BookingForm({ initialReason = '' }) {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(() => createInitialValues(initialReason));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState(() => createAvailabilityState());
  const [now, setNow] = useState(() => new Date());
  const bookingFlowRef = useRef(null);
  const confirmationRef = useRef(null);
  const minDate = todayIso(now);
  const activeAvailability = availability.date === values.date ? availability : createAvailabilityState(values.date);
  const availableTimeSlots = useMemo(() => {
    if (!values.date || values.date < minDate || isClinicClosedDate(values.date)) return [];

    const unavailable = availability.date === values.date ? new Set(availability.unavailableSlots) : new Set();
    return timeSlots.filter((slot) => !unavailable.has(slot) && !isPastTimeSlot(values.date, slot, now));
  }, [availability.date, availability.unavailableSlots, minDate, now, values.date]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setValues((current) => {
      if (current.reason || !initialReason) return current;
      return { ...current, reason: initialReason };
    });
  }, [initialReason]);

  useEffect(() => {
    if (!values.date || values.date < minDate || isClinicClosedDate(values.date)) {
      setAvailability(createAvailabilityState(values.date));
      return undefined;
    }

    let isCurrentRequest = true;
    const selectedDate = values.date;

    setAvailability({
      date: selectedDate,
      loading: true,
      enabled: false,
      unavailableSlots: [],
      message: ''
    });

    fetchBookingAvailability(selectedDate).then((result) => {
      if (!isCurrentRequest) return;

      const unavailableSlots = result.ok ? result.unavailableSlots : [];
      setAvailability({
        date: selectedDate,
        loading: false,
        enabled: result.enabled,
        unavailableSlots,
        message: result.ok ? '' : result.message
      });

      setValues((current) => {
        if (current.date !== selectedDate || !unavailableSlots.includes(current.time)) {
          return current;
        }

        return { ...current, time: '' };
      });
    });

    return () => {
      isCurrentRequest = false;
    };
  }, [minDate, values.date]);

  useEffect(() => {
    if (!values.time || availableTimeSlots.includes(values.time)) return;
    setValues((current) => (current.time === values.time ? { ...current, time: '' } : current));
  }, [availableTimeSlots, values.time]);

  useEffect(() => {
    if (!status?.ok || !confirmationRef.current) return;

    const timeoutId = window.setTimeout(() => {
      const confirmation = confirmationRef.current;
      if (!confirmation) return;

      confirmation.focus({ preventScroll: true });

      const confirmationTop = confirmation.getBoundingClientRect().top + window.scrollY;
      const headerClearance = 120;
      const centerOffset = Math.max(headerClearance, (window.innerHeight - confirmation.offsetHeight) / 2);
      window.scrollTo({
        top: Math.max(0, confirmationTop - centerOffset),
        behavior: 'auto'
      });

      window.requestAnimationFrame(() => {
        const currentTop = confirmation.getBoundingClientRect().top;
        if (currentTop < headerClearance) {
          window.scrollBy({ top: currentTop - headerClearance, behavior: 'auto' });
        }
      });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [status]);

  function update(field, value) {
    setValues((current) => ({
      ...current,
      [field]: value,
      ...(field === 'date' ? { time: '' } : {})
    }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      delete next.step;
      return next;
    });
  }

  function scrollBookingFlowIntoView() {
    const target = bookingFlowRef.current;
    if (!target) return;

    window.requestAnimationFrame(() => {
      const top = target.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    });
  }

  function validateStep(index) {
    const nextErrors = {};

    if (index === 0 && !values.reason) {
      nextErrors.reason = 'Choose a reason for your visit.';
    }

    if (index === 1) {
      if (!values.date) nextErrors.date = 'Choose a preferred date.';
      if (values.date && values.date < minDate) nextErrors.date = 'Choose today or a future date.';
      if (values.date && isClinicClosedDate(values.date)) nextErrors.date = 'The clinic is closed on Wednesdays. Choose another date.';

      if (values.date && isClinicClosedDate(values.date)) {
        nextErrors.time = 'No appointment slots are available on Wednesdays.';
      } else if (values.time && isPastTimeSlot(values.date, values.time, now)) {
        nextErrors.time = 'Choose a future appointment time.';
      } else if (values.date && values.date >= minDate && activeAvailability.loading) {
        nextErrors.time = 'Checking available times. Please wait a moment.';
      } else if (values.date && values.date >= minDate && activeAvailability.unavailableSlots.includes(values.time)) {
        nextErrors.time = 'This slot is no longer available. Choose another time.';
      } else if (values.date && values.date >= minDate && availableTimeSlots.length === 0) {
        nextErrors.time = 'All appointment times are booked for this date. Choose another date.';
      } else if (!values.time) {
        nextErrors.time = 'Choose a preferred time.';
      }
    }

    if (index === 2) {
      if (!values.name.trim()) nextErrors.name = 'Enter your full name.';
      if (!values.email.trim()) nextErrors.email = 'Enter your email address.';
      if (values.email && !isEmail(values.email)) nextErrors.email = 'Enter a valid email address.';
      if (!values.phone.trim()) nextErrors.phone = 'Enter your phone number.';
      if (values.phone && !isPhone(values.phone)) nextErrors.phone = 'Enter a valid phone number.';
    }

    if (index === 3 && !values.consent) {
      nextErrors.consent = 'Consent is required before we can process the request.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function nextStep() {
    if (!validateStep(step)) return;
    setStep((current) => Math.min(current + 1, 3));
    scrollBookingFlowIntoView();
  }

  function previousStep() {
    setStatus(null);
    setStep((current) => Math.max(current - 1, 0));
    scrollBookingFlowIntoView();
  }

  function resetBooking() {
    setStep(0);
    setValues(createInitialValues(initialReason));
    setErrors({});
    setStatus(null);
    setSubmitting(false);
    setAvailability(createAvailabilityState());
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!validateStep(3)) return;

    setSubmitting(true);
    setStatus(null);

    const result = await submitRequest('booking', {
      reason: bookingReasons.find((reason) => reason.value === values.reason)?.label || values.reason,
      date: values.date,
      time: values.time,
      name: values.name.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      notes: values.notes.trim(),
      consent: values.consent,
      website: values.website,
      formStartedAt: values.formStartedAt
    });

    setSubmitting(false);
    setStatus(result);

    if (result.ok) {
      return;
    }

    if (result.code === 'SLOT_UNAVAILABLE' || result.details?.some((detail) => detail.field === 'time')) {
      setStep(1);
      setValues((current) => ({ ...current, time: '' }));
      setErrors({ time: result.details?.find((detail) => detail.field === 'time')?.message || result.message });
      setAvailability((current) => {
        if (current.date !== values.date || !values.time) {
          return current;
        }

        return {
          ...current,
          loading: false,
          enabled: true,
          unavailableSlots: Array.from(new Set([...current.unavailableSlots, values.time]))
        };
      });
    }
  }

  if (status?.ok) {
    return (
      <div className="booking-confirmation" ref={confirmationRef} role="status" tabIndex={-1}>
        <p className="booking-confirmation__eyebrow">Request sent</p>
        <h3 className="heading-3">Appointment requested</h3>
        <p>{status.message}</p>
        <button type="button" className="btn btn--text" onClick={resetBooking}>
          Book another appointment
        </button>
      </div>
    );
  }

  return (
    <div className="booking-flow" ref={bookingFlowRef}>
      <div className="booking-progress" aria-label="Booking progress">
        {[0, 1, 2, 3].map((number) => (
          <div key={number} className={`booking-progress__step ${number === step ? 'is-active' : ''} ${number < step ? 'is-completed' : ''}`}>
            <div className="booking-progress__dot">{number + 1}</div>
            {number < 3 ? <div className="booking-progress__line" /> : null}
          </div>
        ))}
      </div>

      <form className="booking-form" id="booking-form" noValidate onSubmit={handleSubmit}>
        <div className="form-field form-field--spam-trap" aria-hidden="true">
          <label htmlFor="booking-form-reference">Form reference</label>
          <input
            id="booking-form-reference"
            name="form_reference"
            tabIndex={-1}
            autoComplete="new-password"
            value={values.website}
            onChange={(event) => update('website', event.target.value)}
          />
        </div>

        {status ? (
          <div className={`booking-status ${status.ok ? 'is-success' : 'is-error'}`} role="status">
            <p className="heading-3">{statusTitle(status)}</p>
            <p>{status.message}</p>
          </div>
        ) : null}

        <div className={`booking-step ${step === 0 ? 'is-active' : ''}`} hidden={step !== 0}>
          <p className="booking-step__title">Reason for visit</p>
          <p className="booking-step__subtitle">Choose the closest option. If you are unsure, select the last option.</p>
          <div className="form-field">
            <label className="form-field__label" htmlFor="booking-reason">Treatment or concern</label>
            <select
              id="booking-reason"
              className={`form-field__input booking-reason-select ${errors.reason ? 'is-error' : ''}`}
              value={values.reason}
              onChange={(event) => update('reason', event.target.value)}
              aria-describedby={errors.reason ? 'booking-reason-error' : undefined}
              required
            >
              <option value="">Choose a treatment or concern</option>
              {bookingReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>
          {errors.reason ? <p id="booking-reason-error" className="form-field__error is-visible">{errors.reason}</p> : null}
          <div className="booking-nav">
            <span />
            <button type="button" className="btn btn--primary" onClick={nextStep}>
              Continue
            </button>
          </div>
        </div>

        <div className={`booking-step ${step === 1 ? 'is-active' : ''}`} hidden={step !== 1}>
          <p className="booking-step__title">Date and time</p>
          <p className="booking-step__subtitle">Choose your preferred appointment date and time.</p>
          <div className="date-time-grid">
            <div className="form-field">
              <label className="form-field__label" htmlFor="booking-date">Preferred date</label>
              <input
                type="date"
                id="booking-date"
                className={`form-field__input ${errors.date ? 'is-error' : ''}`}
                min={minDate}
                value={values.date}
                onChange={(event) => update('date', event.target.value)}
                required
              />
              {errors.date ? <p className="form-field__error is-visible">{errors.date}</p> : null}
            </div>
            <div className="form-field">
              <label className="form-field__label">Preferred time</label>
              {!values.date ? <p className="time-slots__note">Choose a date first to see available times.</p> : null}
              {values.date && activeAvailability.loading ? <p className="time-slots__note" aria-live="polite">Checking booked slots...</p> : null}
              {values.date && !activeAvailability.loading && activeAvailability.message ? <p className="time-slots__note is-warning">{activeAvailability.message}</p> : null}
              {values.date && isClinicClosedDate(values.date) ? <p className="time-slots__note is-warning">The clinic is closed on Wednesdays. Please choose another date.</p> : null}
              {values.date && !isClinicClosedDate(values.date) && !activeAvailability.loading && availableTimeSlots.length === 0 ? (
                <p className="time-slots__note is-warning">
                  {values.date === minDate ? 'No more appointment times are available today. Please choose another date.' : 'All slots are booked for this date. Please choose another date.'}
                </p>
              ) : null}
              {values.date && !activeAvailability.loading && availableTimeSlots.length > 0 ? (
                <div className={`time-slots ${errors.time ? 'is-error' : ''}`} aria-label="Preferred time">
                  {availableTimeSlots.map((slot) => (
                    <button key={slot} type="button" className={`time-slot ${values.time === slot ? 'is-selected' : ''}`} onClick={() => update('time', slot)}>
                      {slot}
                    </button>
                  ))}
                </div>
              ) : null}
              {errors.time ? <p className="form-field__error is-visible">{errors.time}</p> : null}
            </div>
          </div>
          <div className="booking-nav">
            <button type="button" className="btn btn--text" onClick={previousStep}>
              Back
            </button>
            <button type="button" className="btn btn--primary" onClick={nextStep} disabled={activeAvailability.loading}>
              Continue
            </button>
          </div>
        </div>

        <div className={`booking-step ${step === 2 ? 'is-active' : ''}`} hidden={step !== 2}>
          <p className="booking-step__title">Your details</p>
          <p className="booking-step__subtitle">We need a few details to confirm your appointment.</p>

          <div className="form-field">
            <label className="form-field__label" htmlFor="patient-name">Full name</label>
            <input id="patient-name" className={`form-field__input ${errors.name ? 'is-error' : ''}`} value={values.name} onChange={(event) => update('name', event.target.value)} autoComplete="name" required />
            {errors.name ? <p className="form-field__error is-visible">{errors.name}</p> : null}
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="patient-email">Email address</label>
            <input id="patient-email" type="email" className={`form-field__input ${errors.email ? 'is-error' : ''}`} value={values.email} onChange={(event) => update('email', event.target.value)} autoComplete="email" required />
            {errors.email ? <p className="form-field__error is-visible">{errors.email}</p> : null}
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="patient-phone">Phone number</label>
            <input id="patient-phone" type="tel" className={`form-field__input ${errors.phone ? 'is-error' : ''}`} value={values.phone} onChange={(event) => update('phone', event.target.value)} autoComplete="tel" required />
            {errors.phone ? <p className="form-field__error is-visible">{errors.phone}</p> : null}
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="patient-notes">Additional notes <span className="muted-label">(optional)</span></label>
            <textarea id="patient-notes" className="form-field__input" value={values.notes} onChange={(event) => update('notes', event.target.value)} rows="3" />
          </div>

          <div className="booking-nav">
            <button type="button" className="btn btn--text" onClick={previousStep}>
              Back
            </button>
            <button type="button" className="btn btn--primary" onClick={nextStep}>
              Continue
            </button>
          </div>
        </div>

        <div className={`booking-step ${step === 3 ? 'is-active' : ''}`} hidden={step !== 3}>
          <p className="booking-step__title">Confirm your appointment</p>
          <p className="booking-step__subtitle">Please review your details before submitting.</p>

          <div className="booking-summary">
            <div className="booking-summary__row"><span className="booking-summary__label">Reason for visit</span><span className="booking-summary__value">{bookingReasons.find((reason) => reason.value === values.reason)?.label || '-'}</span></div>
            <div className="booking-summary__row"><span className="booking-summary__label">Date</span><span className="booking-summary__value">{values.date || '-'}</span></div>
            <div className="booking-summary__row"><span className="booking-summary__label">Time</span><span className="booking-summary__value">{values.time || '-'}</span></div>
            <div className="booking-summary__row"><span className="booking-summary__label">Name</span><span className="booking-summary__value">{values.name || '-'}</span></div>
            <div className="booking-summary__row"><span className="booking-summary__label">Email</span><span className="booking-summary__value">{values.email || '-'}</span></div>
            <div className="booking-summary__row"><span className="booking-summary__label">Phone</span><span className="booking-summary__value">{values.phone || '-'}</span></div>
          </div>

          <div className={`consent-field ${errors.consent ? 'is-error' : ''}`}>
            <input id="booking-consent" type="checkbox" checked={values.consent} onChange={(event) => update('consent', event.target.checked)} />
            <label htmlFor="booking-consent" className="consent-field__text">
              I agree to receive appointment updates by phone, email, or WhatsApp, and consent to My Dentist using these details to process this request.
            </label>
          </div>
          {errors.consent ? <p className="form-field__error is-visible">{errors.consent}</p> : null}

          <div className="booking-nav">
            <button type="button" className="btn btn--text" onClick={previousStep} disabled={submitting}>
              Back
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Sending...' : 'Confirm Appointment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
